import { authService } from '../auth/authService';

const FUNCTION_APP_URL =
  'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';

class RasterAssetsService {
  constructor() {
    this.baseUrl = `${FUNCTION_APP_URL}/api/tiling_assets`;
    this.cogProxyUrl = `${FUNCTION_APP_URL}/api/tiling_cog_proxy`;
    /** @type {Map<number, { cogUrl: string, expiresAt: number }>} ms expiry */
    this._resolveCache = new Map();
  }

  /**
   * URL for Range-based COG reads through the API (avoids blob storage CORS in the browser).
   * @param {number} assetId
   */
  getCogProxyUrl(assetId) {
    return `${this.cogProxyUrl}?id=${encodeURIComponent(String(assetId))}`;
  }

  /**
   * XYZ orthophoto via BFF (Bearer); server forwards to RasterTileApi with function key.
   */
  getRasterTileProxyUrl(assetId, z, x, y) {
    const a = encodeURIComponent(String(assetId));
    const zz = encodeURIComponent(String(z));
    const xx = encodeURIComponent(String(x));
    const yy = encodeURIComponent(String(y));
    return `${FUNCTION_APP_URL}/api/raster_tiles_proxy/${a}/${zz}/${xx}/${yy}`;
  }

  /**
   * GDAL warped orthophoto window in native SRID (same bbox/grid as DEM). BFF proxies to RasterTileApi when configured.
   * @param {number} assetId
   * @param {{ minX: number, minY: number, maxX: number, maxY: number }} bounds native CRS
   * @param {number} srid e.g. 26908
   * @param {number} width raster width (match DEM grid)
   * @param {number} height raster height
   */
  getOrthophotoWindowProxyUrl(assetId, bounds, srid, width, height) {
    const q = new URLSearchParams({
      asset_id: String(assetId),
      min_x: String(bounds.minX),
      min_y: String(bounds.minY),
      max_x: String(bounds.maxX),
      max_y: String(bounds.maxY),
      srid: String(srid),
      width: String(width),
      height: String(height),
    });
    return `${FUNCTION_APP_URL}/api/raster_ortho_window?${q.toString()}`;
  }

  /** Single-asset catalog JSON URL (tiling_assets?id=). */
  getTilingAssetDetailUrl(assetId) {
    return `${this.baseUrl}?id=${encodeURIComponent(String(assetId))}`;
  }

  async getAuthenticatedHeaders() {
    const token = await authService.getAccessToken();
    const userInfo = authService.getUserInfo();
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'x-user-id': userInfo?.email || userInfo?.username || 'unknown',
    };
  }

  /**
   * List processed COG assets (no raw blob URL).
   * @returns {Promise<Array<{ id: number, title: string, nativeCrs: string|null, bounds: object, ... }>>}
   */
  async listAssets() {
    const headers = await this.getAuthenticatedHeaders();
    const response = await fetch(this.baseUrl, { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`tiling_assets list failed: ${response.status} ${text}`);
    }
    const result = await response.json();
    return result.data || [];
  }

  _parseExpiryMs(iso) {
    if (!iso) return 0;
    const t = Date.parse(iso);
    return Number.isFinite(t) ? t : 0;
  }

  /**
   * Resolve SAS URL for an asset id; refreshes when within refreshSkewMs of expiry.
   * @param {number} assetId
   * @param {number} [refreshSkewMs=120000] refresh if expires within 2 minutes
   */
  async resolveCogUrl(assetId, refreshSkewMs = 120000) {
    const now = Date.now();
    const cached = this._resolveCache.get(assetId);
    if (cached && cached.expiresAt - refreshSkewMs > now) {
      return cached.cogUrl;
    }

    const headers = await this.getAuthenticatedHeaders();
    const url = `${this.baseUrl}?id=${encodeURIComponent(String(assetId))}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`tiling_assets resolve failed: ${response.status} ${text}`);
    }
    const result = await response.json();
    const asset = result.asset;
    if (!asset?.cogUrl) {
      throw new Error('resolve response missing cogUrl');
    }
    const expiresAt = this._parseExpiryMs(asset.expiresAt);
    this._resolveCache.set(assetId, { cogUrl: asset.cogUrl, expiresAt });
    return asset.cogUrl;
  }

  clearResolveCache() {
    this._resolveCache.clear();
  }
}

export const rasterAssetsService = new RasterAssetsService();
export default rasterAssetsService;
